import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { 
  House, 
  Users, 
  Package, 
  Globe, 
  Database, 
  Gear,
  SignOut,
  X,
  Truck
} from '@phosphor-icons/react';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { isOpen, isMobile, closeSidebar } = useSidebar();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
    closeSidebar();
  };

  const handleNavClick = () => {
    if (isMobile) {
      closeSidebar();
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeSidebar}
          data-testid="sidebar-overlay"
        />
      )}
      
      <aside 
        className={`fixed left-0 top-0 h-screen w-64 bg-[#13161e] border-r border-[#2a2e3d] flex flex-col z-50 transition-transform duration-300 ease-in-out ${
          isMobile ? (isOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'
        }`} 
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-[#2a2e3d]">
          <div className="flex items-center justify-between">
            <img 
              src="/logo-visionweb.png" 
              alt="VisionWeb" 
              className="h-8 object-contain"
              data-testid="sidebar-logo"
            />
            {isMobile && (
              <button 
                onClick={closeSidebar}
                className="text-[#8b949e] hover:text-white transition-colors md:hidden"
                data-testid="close-sidebar"
              >
                <X size={24} />
              </button>
            )}
          </div>
          <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs mt-1" data-testid="version-text">VISIONWEB · v1.0.0</div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
          <div>
            <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider mb-2">General</div>
            <NavLink
              to="/dashboard"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-sm transition-colors ${
                  isActive
                    ? 'bg-[#4f9eff]/10 text-[#4f9eff] border-l-2 border-[#4f9eff]'
                    : 'text-[#8b949e] hover:text-white hover:bg-[#1a1e28]'
                }`
              }
              data-testid="nav-dashboard"
            >
              <House size={18} weight="duotone" />
              Dashboard
            </NavLink>
          </div>

          <div>
            <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider mb-2">Módulos</div>
            <NavLink
              to="/clientes"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-sm transition-colors ${
                  isActive
                    ? 'bg-[#4f9eff]/10 text-[#4f9eff] border-l-2 border-[#4f9eff]'
                    : 'text-[#8b949e] hover:text-white hover:bg-[#1a1e28]'
                }`
              }
              data-testid="nav-clientes"
            >
              <Users size={18} weight="duotone" />
              Clientes
            </NavLink>
            <NavLink
              to="/productos"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-sm transition-colors ${
                  isActive
                    ? 'bg-[#4f9eff]/10 text-[#4f9eff] border-l-2 border-[#4f9eff]'
                    : 'text-[#8b949e] hover:text-white hover:bg-[#1a1e28]'
                }`
              }
              data-testid="nav-productos"
            >
              <Package size={18} weight="duotone" />
              Hosting
            </NavLink>
            <NavLink
              to="/servicios"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-sm transition-colors ${
                  isActive
                    ? 'bg-[#4f9eff]/10 text-[#4f9eff] border-l-2 border-[#4f9eff]'
                    : 'text-[#8b949e] hover:text-white hover:bg-[#1a1e28]'
                }`
              }
              data-testid="nav-servicios"
            >
              <Gear size={18} weight="duotone" />
              Servicios
            </NavLink>
            <NavLink
              to="/dominios"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-sm transition-colors ${
                  isActive
                    ? 'bg-[#4f9eff]/10 text-[#4f9eff] border-l-2 border-[#4f9eff]'
                    : 'text-[#8b949e] hover:text-white hover:bg-[#1a1e28]'
                }`
              }
              data-testid="nav-dominios"
            >
              <Globe size={18} weight="duotone" />
              Dominios
            </NavLink>
            <NavLink
              to="/proveedores"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-sm transition-colors ${
                  isActive
                    ? 'bg-[#4f9eff]/10 text-[#4f9eff] border-l-2 border-[#4f9eff]'
                    : 'text-[#8b949e] hover:text-white hover:bg-[#1a1e28]'
                }`
              }
              data-testid="nav-proveedores"
            >
              <Truck size={18} weight="duotone" />
              Egresos
            </NavLink>
          </div>

          <div>
            <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider mb-2">Sistema</div>
            <NavLink
              to="/arquitectura"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-sm transition-colors ${
                  isActive
                    ? 'bg-[#4f9eff]/10 text-[#4f9eff] border-l-2 border-[#4f9eff]'
                    : 'text-[#8b949e] hover:text-white hover:bg-[#1a1e28]'
                }`
              }
              data-testid="nav-arquitectura"
            >
              <Database size={18} weight="duotone" />
              Arquitectura DB
            </NavLink>
            <NavLink
              to="/stack"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-sm transition-colors ${
                  isActive
                    ? 'bg-[#4f9eff]/10 text-[#4f9eff] border-l-2 border-[#4f9eff]'
                    : 'text-[#8b949e] hover:text-white hover:bg-[#1a1e28]'
                }`
              }
              data-testid="nav-stack"
            >
              <Gear size={18} weight="duotone" />
              Stack LAMP
            </NavLink>
          </div>

          {/* Mobile Logout Button */}
          {isMobile && (
            <div className="pt-4 mt-4 border-t border-[#2a2e3d]">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2 w-full rounded-sm font-['IBM_Plex_Mono'] text-sm text-[#ff5252] hover:bg-[#ff5252]/10 transition-colors"
                data-testid="mobile-logout-button"
              >
                <SignOut size={18} weight="duotone" />
                SALIR
              </button>
            </div>
          )}
        </nav>

        {/* User Footer - Only on Desktop */}
        {!isMobile && (
          <div className="p-4 border-t border-[#2a2e3d]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#b06eff] flex items-center justify-center font-['IBM_Plex_Mono'] text-white font-bold">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-['Mulish'] text-sm truncate" data-testid="user-name">{user?.name || 'Admin'}</div>
                <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs truncate" data-testid="user-role">{user?.role || 'Administrador'}</div>
              </div>
              <button
                onClick={handleLogout}
                className="text-[#8b949e] hover:text-[#ff5252] transition-colors flex-shrink-0"
                data-testid="logout-button"
              >
                <SignOut size={20} />
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;